import React from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Download, RotateCcw } from 'lucide-react';
import type { ImportStats, ImportError } from '../types';

interface ImportProgressProps {
  stats: ImportStats;
  isImporting: boolean;
  isComplete: boolean;
  errors: ImportError[];
  onRetry?: () => void;
  onDownloadErrors?: () => void;
}

const ImportProgress: React.FC<ImportProgressProps> = ({
  stats,
  isImporting,
  isComplete,
  errors,
  onRetry,
  onDownloadErrors
}) => {
  const hasErrors = errors.length > 0;
  const successRate = stats.total > 0 ? ((stats.imported / stats.total) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {isImporting ? 'Importing Leads...' : isComplete ? 'Import Complete' : 'Ready to Import'}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isImporting
            ? 'Please wait while we import your leads. This may take a few moments.'
            : isComplete
            ? 'Your leads have been processed. Review the results below.'
            : 'Click "Start Import" to begin importing your leads.'}
        </p>
      </div>

      {/* Progress Bar */}
      {isImporting && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-900 dark:text-white">
              Progress: {stats.imported} / {stats.total}
            </span>
            <span className="font-semibold text-blue-600 dark:text-blue-400">{stats.progress}%</span>
          </div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500 ease-out"
              style={{ width: `${stats.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <Loader2 className={`h-5 w-5 text-blue-600 dark:text-blue-400 ${isImporting ? 'animate-spin' : ''}`} />
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Total</p>
          </div>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.total}</p>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            <p className="text-sm font-medium text-green-900 dark:text-green-100">Imported</p>
          </div>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.imported}</p>
        </div>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">Duplicates</p>
          </div>
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.duplicates}</p>
        </div>

        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm font-medium text-red-900 dark:text-red-100">Errors</p>
          </div>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.errors}</p>
        </div>
      </div>

      {/* Success Message */}
      {isComplete && !hasErrors && (
        <div className="p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="p-3 bg-green-100 dark:bg-green-800 rounded-full">
                <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
                Import Successful!
              </h4>
              <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                {stats.imported} lead{stats.imported !== 1 ? 's' : ''} imported successfully
                {stats.duplicates > 0 && ` (${stats.duplicates} duplicate${stats.duplicates !== 1 ? 's' : ''} skipped)`}.
                Success rate: {successRate}%
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                You can now view and manage your imported leads in the Leads section.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Details */}
      {isComplete && hasErrors && (
        <div className="space-y-4">
          <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="p-3 bg-yellow-100 dark:bg-yellow-800 rounded-full">
                  <AlertTriangle className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                  Import Completed with Errors
                </h4>
                <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
                  {stats.imported} lead{stats.imported !== 1 ? 's' : ''} imported successfully, but {stats.errors} row
                  {stats.errors !== 1 ? 's' : ''} failed. Success rate: {successRate}%
                </p>
                <div className="flex space-x-3">
                  {onDownloadErrors && (
                    <button
                      type="button"
                      onClick={onDownloadErrors}
                      className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download Error Report</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Error List */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Error Details ({errors.length})
              </h4>
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Row
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Field
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Error
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                  {errors.slice(0, 10).map((error, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {error.row}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {error.field || '—'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                        {error.error}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errors.length > 10 && (
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 text-center text-sm text-gray-600 dark:text-gray-400">
                Showing 10 of {errors.length} errors. Download full report for complete details.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Retry Button */}
      {isComplete && hasErrors && onRetry && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            <RotateCcw className="h-5 w-5" />
            <span>Retry Failed Imports</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportProgress;
